import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { Box, Typography, Stack } from '@mui/material';

const MONTH_NAMES = { 1: 'Jan', 2: 'Fév', 3: 'Mar', 4: 'Avr', 5: 'Mai', 6: 'Juin', 7: 'Juil', 8: 'Août', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Déc' };

// Tooltip Moderne Flouté (Glassmorphism)
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box sx={{
        backgroundColor: 'rgba(255, 255, 255, 0.96)',
        backdropFilter: 'blur(8px)',
        border: '1px solid #e5e7eb',
        padding: '10px 14px',
        borderRadius: '12px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)',
      }}>
        <Typography variant="caption" sx={{ color: '#6b7280', fontWeight: 600, display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#10b981' }} />
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827' }}>
            {payload[0].value.toLocaleString('fr-FR')} €
          </Typography>
        </Stack>
      </Box>
    );
  }
  return null;
};

// Fonction personnalisée pour styliser la valeur texte au-dessus des barres
const renderCustomizedLabel = (props) => {
  const { x, y, width, value } = props;
  if (value === 0) return null; // N'affiche rien si le revenu est à 0€ pour alléger le rendu

  return (
    <text 
      x={x + width / 2} 
      y={y - 8} // Positionne le texte 8px au-dessus de la barre
      fill="#4b5563" // Couleur de texte sobre (gris foncé)
      fontSize={11} 
      fontWeight={700} 
      textAnchor="middle"
    >
      {`${value.toLocaleString('fr-FR')}€`}
    </text>
  );
};

export default function RevenueChart({ data }) {
  
  const rollingMonthsData = useMemo(() => {
    const currentMonth = 5; 
    const currentYear = 2026;
    const result = [];
    
    for (let i = 5; i >= 0; i--) {
      let targetMonth = currentMonth - i;
      let targetYear = currentYear;
      
      if (targetMonth <= 0) {
        targetMonth += 12;
        targetYear -= 1;
      }
      
      const match = data?.find(d => d.month === targetMonth);
      
      result.push({
        month: targetMonth,
        year: targetYear,
        name: `${MONTH_NAMES[targetMonth]}`,
        revenue: match ? match.revenue : 0 
      });
    }
    return result;
  }, [data]);

  const stats = useMemo(() => {
    const revenues = rollingMonthsData.map(d => d.revenue);
    const total = revenues.reduce((a, b) => a + b, 0);
    const max = Math.max(...revenues);
    return { total, max };
  }, [rollingMonthsData]);

  if (!data || data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height={300}>
        <Typography variant="body2" color="textSecondary">Aucune donnée disponible</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
      
      <Stack direction="row" spacing={4} sx={{ px: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
            Cumulé (6 mois)
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#111827' }}>
            {stats.total.toLocaleString('fr-FR')} €
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" sx={{ color: '#6b7280', display: 'block', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.025em' }}>
            Meilleure performance
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#10b981' }}>
            {stats.max.toLocaleString('fr-FR')} €
          </Typography>
        </Box>
      </Stack>

      <Box sx={{ width: '100%', height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          {/* Note : top margin passée à 25 pour laisser de la place aux étiquettes de texte */}
          <BarChart data={rollingMonthsData} margin={{ top: 25, right: 5, left: -25, bottom: 0 }} barSize={32}>
            
            <CartesianGrid strokeDasharray="4 4" vertical={false} stroke="#f3f4f6" />
            
            <XAxis 
              dataKey="name" 
              axisLine={false} 
              tickLine={false} 
              tick={{ fontSize: 12, fill: '#9ca3af', fontWeight: 500 }}
            />
            
            <YAxis 
              axisLine={false} 
              tickLine={false} 
              tick={{ fontSize: 12, fill: '#9ca3af', fontWeight: 500 }}
              tickFormatter={(v) => v > 0 ? `${v}€` : '0'}
            />
            
            <Tooltip 
              content={<CustomTooltip />} 
              cursor={{ fill: '#f9fafb', radius: 8 }} 
            />
            
            <Bar 
              dataKey="revenue" 
              radius={[6, 6, 0, 0]} 
              animationDuration={1200}
            >
              {/* ÉTIQUETTE AJOUTÉE : Affiche le montant au-dessus de la barre */}
              <LabelList dataKey="revenue" content={renderCustomizedLabel} />

              {
                rollingMonthsData.map((entry, index) => {
                  const isCurrentMonth = entry.month === 5;
                  return (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={isCurrentMonth ? '#10b981' : '#34d399'} 
                      style={{
                        filter: isCurrentMonth ? 'drop-shadow(0px 4px 8px rgba(16,185,129,0.25))' : 'none',
                        cursor: 'pointer'
                      }}
                    />
                  );
                })
              }
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}
